import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';
import {
    nextSpeciesSummaryQuery,
    nextSpeciesDetailQuery,
    NextSpeciesDetail,
    NextSpeciesDetailQueryResult,
    nextRedListSpeciesQuery,
    nextInvasiveSpeciesQuery,
    NextSpeciesSummary,
    NextSpeciesListResponse,
    NextSpeciesSummaryQueryResult,
    NextRedListSpeciesQueryResult,
    NextInvasiveSpeciesQueryResult,
} from './nextSpeciesQuery';

type SummaryQueryParams = {
    customerId: string;
    filters?: { name: string; values: string[] }[];
};

type DetailQueryParams = SummaryQueryParams & {
    scientificName: string;
};

type ListQueryParams = SummaryQueryParams & {
    pagination: { page: number; pageSize: number };
    sortBy?: { field: string; order: 'ASC' | 'DESC' };
};

/**
 * Sort fields the grid can order by. Go rejects anything else rather than
 * silently falling back, so a column that maps to nothing here would surface an
 * error instead of an arbitrary order — which is why `predictionCreatedAt` has no
 * entry and the new table has no "Added" column.
 */
export const NEXT_SPECIES_SORT_FIELDS = ['scientificName', 'commonName', 'siteCount', 'iucnCategory'] as const;

const nextSpeciesGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        nextSpeciesSummary: builder.query<NextSpeciesSummary, SummaryQueryParams>({
            query: params => ({
                body: nextSpeciesSummaryQuery,
                variables: params,
            }),
            transformResponse: (response: NextSpeciesSummaryQueryResult) => response.portfolio.getPortfolioSpeciesSummary,
        }),
        nextSpeciesDetail: builder.query<NextSpeciesDetail, DetailQueryParams>({
            query: params => ({
                body: nextSpeciesDetailQuery,
                variables: params,
            }),
            transformResponse: (response: NextSpeciesDetailQueryResult) => response.portfolio.getPortfolioSpeciesDetail,
        }),
        nextRedListSpecies: builder.query<NextSpeciesListResponse, ListQueryParams>({
            query: params => ({
                body: nextRedListSpeciesQuery,
                variables: params,
            }),
            transformResponse: (response: NextRedListSpeciesQueryResult) => response.portfolio.getPortfolioRedListSpecies,
        }),
        nextInvasiveSpecies: builder.query<NextSpeciesListResponse, ListQueryParams>({
            query: params => ({
                body: nextInvasiveSpeciesQuery,
                variables: params,
            }),
            transformResponse: (response: NextInvasiveSpeciesQueryResult) => response.portfolio.getPortfolioInvasiveSpecies,
        }),
    }),
});

export const { useNextSpeciesSummaryQuery, useNextRedListSpeciesQuery, useNextInvasiveSpeciesQuery, useNextSpeciesDetailQuery } =
    nextSpeciesGraphSlice;

export default nextSpeciesGraphSlice.reducer;
